/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import {
  ExtendedFlex,
  ExtendedBox,
  Hero,
  ModalWithOverlay,
  ScrollToTopOnMount,
  CarouselLarge,
  Body,
} from '../coreui';

import ConstantStaticData from './data';
import DynamicBrowseJoinLink from '../helpers';
import heroBannerImage from './assets/img_hero_whyreits.jpg';
import ReitInvesting from './reitInvesting';
import ChartAndCopy from '../benefits/components/chartAndCopy';
import RelatedQueueContent from '../knowledgeCenter/components/RelatedQueueContent';
import ComparingReits from './comparingReits';
import InvestmentObjectives from './investmentObjectives';
import InvestToday from './components/InvestToday';
import IconCallouts from '../components/IconCallouts';
import Graph from '../assets/chart_growth.png';
import AvailableToday from '../benefits/components/availableToday';
import Section from '../ioDetails/components/section';

import CarouselContent from '../components/CarouselContent';
import BrowseLink from '../components/BrowseLink';

class WhyReits extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isModalVisible: false,
      modalContent: null,
    };

    this.toggleModal = this.toggleModal.bind(this);
  }

  toggleModal(modalContent) {
    this.setState((state) => ({
      isModalVisible: !state.isModalVisible,
      modalContent: state.isModalVisible ? null : modalContent,
    }));
  }

  render() {
    const {
      isModalVisible,
      modalContent,
    } = this.state;
    const {
      user,
      isLoggedIn,
      reitData,
      mogulReit1DistributionDuration,
      mogulReit1DistributionTooltip,
      mogulReit1LastDistributionDeclared,
      mogulReit2DistributionDuration,
      mogulReit2DistributionTooltip,
      mogulReit2LastDistributionDeclared,
    } = this.props;

    return (
      <ExtendedBox>
        <ScrollToTopOnMount />
        <ExtendedFlex>
          <Hero
            image={heroBannerImage}
            header="Access the full potential of commercial real estate"
            subHeader="Investing in real estate investment trusts (REITs)"
            bgPosition="74%"
          />
        </ExtendedFlex>
        <IconCallouts
          {...ConstantStaticData.basics}
          moreComponent={(
            <ExtendedFlex>
              <DynamicBrowseJoinLink
                isLoggedIn={isLoggedIn}
                anonText="Get Started"
                type="L1"
              />
            </ExtendedFlex>
          )}
          bg="gray.0"
        />
        <InvestmentObjectives />
        <ComparingReits
          toggleModal={this.toggleModal}
        />
        <ChartAndCopy
          toggleModal={this.toggleModal}
          {...ConstantStaticData.performanceHistory}
          chart={Graph}
          backgroundColor="white"
          isLoggedIn={user.isLoggedIn}
        />
        {
          mogulReit1LastDistributionDeclared && mogulReit2LastDistributionDeclared &&
          <AvailableToday
            heading={ConstantStaticData.reitData.heading}
            paragraphs={ConstantStaticData.reitData.paragraphs}
            investments={[reitData.mr1, reitData.mr2]}
            user={user}
          />
        }
        <Section>
          <CarouselLarge>
            <CarouselContent
              heading="MogulREIT I"
              subheading="Our REIT distributions to date"
              body={mogulReit1DistributionDuration ? (
                <>
                  Since MogulREIT I’s inception in 2016, investors have received 51 consecutive months of distributions totaling $13.3MM.
                </>
              ) : undefined}
              cta={(
                <BrowseLink
                  isLoggedIn={isLoggedIn}
                />
              )}
              bigNumber={{ number: mogulReit1LastDistributionDeclared * 100, type: 'percentage' }}
              caption="Annualized distributions (net of fees) as of 2/1/2021"
              disclaimer
              handleOnClick={() => {
                this.toggleModal((
                  <Body
                    type="B1"
                  >
                    {mogulReit1DistributionTooltip}
                  </Body>
                ));
              }}
            />
            <CarouselContent
              heading="MogulREIT II"
              subheading="Our REIT distributions to date"
              body={mogulReit2DistributionDuration ? (
                <>
                  Since MogulREIT II’s inception in 2017, investors have received 12 consecutive quarters of distributions to investors totalling over $2.5MM.
                </>
              ) : undefined}
              cta={(
                <BrowseLink
                  isLoggedIn={isLoggedIn}
                />
              )}
              bigNumber={{ number: mogulReit2LastDistributionDeclared * 100, type: 'percentage' }}
              caption="Annualized distributions (net of fees) as of 12/31/2020"
              disclaimer
              handleOnClick={() => {
                this.toggleModal((
                  <Body
                    type="B1"
                  >
                    {mogulReit2DistributionTooltip}
                  </Body>
                ));
              }}
            />
          </CarouselLarge>
        </Section>
        <ReitInvesting />
        <RelatedQueueContent
          title="Gain New Investing Insight"
          queue="why_reits_articles"
        />
        <InvestToday
          isLoggedIn={user.isLoggedIn}
          showPhone={false}
        />
        <ModalWithOverlay
          isVisible={isModalVisible}
          handleClick={this.toggleModal}
        >
          {modalContent}
        </ModalWithOverlay>
      </ExtendedBox>
    );
  }
}

const mapStateToProps = (state) => ({
  user: state.homepage.user,
  isLoggedIn: state.app.user.isLoggedIn,
  reitData: state.reitData.data,
  mogulReit1DistributionDuration: state.tokens.mogulReit1DistributionDuration,
  mogulReit1DistributionAmount: state.tokens.mogulReit1DistributionAmount,
  mogulReit1DistributionTooltip: state.tokens.mogulReit1DistributionTooltip,
  mogulReit1EffectiveDistributionDate: state.tokens.mogulReit1EffectiveDistributionDate,
  mogulReit2DistributionDuration: state.tokens.mogulReit2DistributionDuration,
  mogulReit2DistributionAmount: state.tokens.mogulReit2DistributionAmount,
  mogulReit2DistributionTooltip: state.tokens.mogulReit2DistributionTooltip,
  mogulReit2EffectiveDistributionDate: state.tokens.mogulReit2EffectiveDistributionDate,
  mogulReit1LastDistributionDeclared: state.tokens.mogulReit1LastDistributionDeclared,
  mogulReit2LastDistributionDeclared: state.tokens.mogulReit2LastDistributionDeclared,
});

WhyReits.propTypes = {
  user: PropTypes.object,
  isLoggedIn: PropTypes.bool,
  reitData: PropTypes.object,
  mogulReit1DistributionDuration: PropTypes.string,
  mogulReit1DistributionAmount: PropTypes.number,
  mogulReit1DistributionTooltip: PropTypes.string,
  mogulReit1EffectiveDistributionDate: PropTypes.number,
  mogulReit2DistributionDuration: PropTypes.string,
  mogulReit2DistributionAmount: PropTypes.number,
  mogulReit2DistributionTooltip: PropTypes.string,
  mogulReit2EffectiveDistributionDate: PropTypes.number,
  mogulReit1LastDistributionDeclared: PropTypes.number,
  mogulReit2LastDistributionDeclared: PropTypes.number,
};

export default connect(mapStateToProps)(WhyReits);
